// AICA Command Palette Component
// Global command palette (⌘K) for navigation and actions
import { m } from 'framer-motion';
import { cn, formatDateLocale } from '@/lib/utils';
import { fadeVariants, scaleVariants, buildTransition } from '@/components/transitions';
import {
  Search,
  X,
  ArrowRight,
  Plus,
  FileAudio,
  LayoutDashboard,
  Settings,
  ShieldAlert,
  FileBarChart,
  CreditCard,
  Users,
  ScrollText,
  Upload,
  Download,
  BarChart3,
  Clock,
  FolderOpen,
  Table2,
  PhoneMissed,
  MessagesSquare,
  ListChecks,
  ThumbsUp,
  CheckSquare,
  Award,
  BookOpen,
  Loader2,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Workflow,
  Shuffle,
  ClipboardCheck,
  Database,
  Sparkles,
  Handshake,
  BookOpenCheck,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { isAssistantScopeReleased } from '@aica/api-types/schemas';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { useNavigation } from '@/contexts/NavigationContext';
import { useRole } from '@/contexts/RoleContext';
import { useFeatureFlags, type FeatureFlags } from '@/contexts/FeatureFlagsContext';
import { SCREEN_FEATURE_FLAGS, navItemAccess } from '@/components/layout/sidebar-config';
import { minPlanLabel } from '@/lib/product-package-label';
import { useTrialOpened } from '@/hooks/useTrialOpened';
import { PRO_TRIAL_PLAN } from '@/lib/pro-trial';
import { usePersistedState } from '@/hooks/usePersistedState';
import { useDebounce } from '@/hooks/useDebounce';
import { useApiClient } from '@/hooks/useApiClient';
import type { RecordItem } from '@/lib/api/mappers';
import { toRecordItems } from '@/lib/api/mappers';
import { logError } from '@/lib/logger';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

interface CommandItem {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  icon: LucideIcon;
  action: () => void;
  category: 'navigation' | 'action' | 'recent';
  keywords?: string[];
  shortcutKey?: string;
  /** Screen id from route-permissions.SCREEN_ACCESS — hides the command from roles that can't open the page */
  screen?: string;
  /**
   * Feature flag gating the command. Usually OMIT this: commands with a
   * `screen` inherit the sidebar item's flag via SCREEN_FEATURE_FLAGS, so the
   * palette can't drift from nav gating. Set it only when the command is
   * gated by a flag different from its screen's (or has no screen).
   */
  featureFlag?: keyof FeatureFlags;
  /**
   * Capability required on top of screen visibility. Screen access and the
   * right to act are not the same: an agent-tier role may see a screen while
   * the API answers 403 for its primary action, and screen gating alone would
   * still advertise a dead end.
   */
  capability?: 'createProject' | 'uploadRecords';
  /** Set when the plan is below the command's feature: listed with a lock and the plan name. */
  lockedFlag?: keyof FeatureFlags;
  /** Set when only the Pro trial opens the command's feature (#2816): listed «Pro · trial». */
  trialFlag?: keyof FeatureFlags;
  /**
   * Not listed with a lock: its destination does not say what the plan lacks
   * (a step that sends a closed gate back to the overview), so the lock would
   * lead nowhere.
   */
  hiddenWhenLocked?: boolean;
}

type NavigateFn = (to: string) => void;

const getNavigationCommands = (navigate: NavigateFn): CommandItem[] => [
  {
    id: 'nav-dashboard',
    labelKey: 'nav.dashboard',
    descriptionKey: 'commandPalette.descriptions.dashboard',
    icon: LayoutDashboard,
    category: 'navigation',
    screen: 'dashboard',
    keywords: ['dashboard', 'home', 'overview', 'головна', 'дашборд', 'главная'],
    shortcutKey: '⌘1',
    action: () => navigate('/dashboard'),
  },
  {
    id: 'nav-records',
    labelKey: 'nav.records',
    descriptionKey: 'commandPalette.descriptions.records',
    icon: FileAudio,
    category: 'navigation',
    screen: 'records',
    keywords: ['records', 'audio', 'transcripts', 'записи', 'аудіо'],
    shortcutKey: '⌘2',
    action: () => navigate('/records'),
  },
  {
    id: 'nav-records-table',
    labelKey: 'nav.recordsTable',
    descriptionKey: 'commandPalette.descriptions.recordsTable',
    icon: Table2,
    category: 'navigation',
    screen: 'records',
    keywords: ['records', 'table', 'grid', 'bloomberg', 'таблица', 'таблиця'],
    shortcutKey: 'g t',
    action: () => navigate('/records?view=table'),
  },
  {
    id: 'nav-deals',
    labelKey: 'nav.deals',
    descriptionKey: 'commandPalette.descriptions.deals',
    icon: Handshake,
    category: 'navigation',
    // The flag comes with the screen (SCREEN_FEATURE_FLAGS: the sidebar item's).
    screen: 'deals',
    keywords: ['deals', 'pipeline', 'at risk', 'сделки', 'угоди', 'риск', 'negocios'],
    action: () => navigate('/deals'),
  },
  {
    id: 'nav-alerts',
    labelKey: 'nav.alerts',
    descriptionKey: 'commandPalette.descriptions.alerts',
    icon: ShieldAlert,
    category: 'navigation',
    screen: 'alerts',
    keywords: ['alerts', 'сигналы', 'сигнали', 'алерты', 'алерти'],
    shortcutKey: 'g l',
    action: () => navigate('/alerts'),
  },
  {
    id: 'nav-projects',
    labelKey: 'nav.projects',
    descriptionKey: 'commandPalette.descriptions.projects',
    icon: FolderOpen,
    category: 'navigation',
    screen: 'projects',
    keywords: ['projects', 'list', 'проєкти', 'проекты'],
    shortcutKey: '⌘3',
    action: () => navigate('/projects'),
  },
  {
    id: 'nav-billing',
    labelKey: 'nav.billing',
    descriptionKey: 'commandPalette.descriptions.billing',
    icon: CreditCard,
    category: 'navigation',
    screen: 'billing',
    keywords: ['billing', 'payment', 'invoice', 'рахунки', 'счета'],
    shortcutKey: 'g b',
    action: () => navigate('/settings?section=billing'),
  },
  {
    id: 'nav-call-stats',
    labelKey: 'nav.callStatistics',
    descriptionKey: 'commandPalette.descriptions.callStatistics',
    icon: BarChart3,
    category: 'navigation',
    screen: 'call-stats',
    keywords: ['call', 'statistics', 'analytics', 'дзвінки', 'звонки', 'статистика'],
    action: () => navigate('/analytics/call-stats'),
  },
  {
    id: 'nav-missed-calls',
    labelKey: 'nav.missedCalls',
    descriptionKey: 'commandPalette.descriptions.missedCalls',
    icon: PhoneMissed,
    category: 'navigation',
    screen: 'missed-calls',
    keywords: ['missed', 'calls', 'abandon', 'пропущенные', 'пропущені'],
    action: () => navigate('/analytics/missed-calls'),
  },
  {
    // «Голос клиента» (#848) opens on Sales — the frequent questions, the
    // questions without an answer and why customers decline (#843, #844) live
    // there; the old «customer questions» command led to a page that is now a
    // redirect. The flag comes with the screen.
    id: 'nav-voice-of-customer',
    labelKey: 'nav.voiceOfCustomer',
    descriptionKey: 'commandPalette.descriptions.voiceOfCustomer',
    icon: MessagesSquare,
    category: 'navigation',
    screen: 'voice-of-customer',
    keywords: [
      'voice',
      'customer',
      'questions',
      'faq',
      'declines',
      'objections',
      'голос',
      'вопросы',
      'отказы',
      'питання',
      'відмови',
      'preguntas',
      'rechazos',
    ],
    action: () => navigate('/analytics/voice'),
  },
  {
    id: 'nav-script-steps',
    labelKey: 'nav.scriptSteps',
    descriptionKey: 'commandPalette.descriptions.scriptSteps',
    icon: ListChecks,
    category: 'navigation',
    screen: 'script-steps',
    keywords: [
      'script',
      'steps',
      'adherence',
      'скрипт',
      'шаги',
      'кроки',
      'guion',
      'pasos',
    ],
    action: () => navigate('/analytics/script-steps'),
  },
  {
    id: 'nav-ai-feedback',
    labelKey: 'nav.aiFeedback',
    descriptionKey: 'commandPalette.descriptions.aiFeedback',
    icon: ThumbsUp,
    category: 'navigation',
    screen: 'ai-feedback',
    keywords: ['ai', 'feedback', 'quality', 'оценка', 'фідбек'],
    action: () => navigate('/analytics/ai-feedback'),
  },
  {
    id: 'nav-ai-tasks',
    labelKey: 'nav.aiTasks',
    descriptionKey: 'commandPalette.descriptions.aiTasks',
    icon: CheckSquare,
    category: 'navigation',
    screen: 'ai-tasks',
    keywords: ['ai', 'tasks', 'todo', 'задачи', 'завдання'],
    action: () => navigate('/analytics/ai-tasks'),
  },
  {
    id: 'nav-competitions',
    labelKey: 'nav.competitions',
    descriptionKey: 'commandPalette.descriptions.competitions',
    icon: Award,
    category: 'navigation',
    screen: 'competitions',
    keywords: ['competitions', 'leaderboard', 'соревнования', 'змагання'],
    action: () => navigate('/competitions'),
  },
  {
    id: 'nav-knowledge-base',
    labelKey: 'nav.knowledgeBase',
    descriptionKey: 'commandPalette.descriptions.knowledgeBase',
    icon: BookOpen,
    category: 'navigation',
    screen: 'knowledge-base',
    keywords: ['knowledge', 'base', 'kb', 'база', 'знаний'],
    action: () => navigate('/knowledge-base'),
  },
  {
    id: 'nav-reports',
    labelKey: 'nav.reports',
    descriptionKey: 'commandPalette.descriptions.reports',
    icon: FileBarChart,
    category: 'navigation',
    // The only reports surface is /admin/reports (owner-only), so gate by the
    // owner-only 'admin' screen — 'reports' would show a dead command to client/admin.
    screen: 'admin',
    keywords: ['reports', 'analytics', 'звіти', 'отчёты'],
    action: () => navigate('/admin/reports'),
  },
  {
    id: 'nav-team',
    labelKey: 'nav.users',
    descriptionKey: 'commandPalette.descriptions.team',
    icon: Users,
    category: 'navigation',
    screen: 'users',
    keywords: [
      'team',
      'users',
      'members',
      'agents',
      'команда',
      'користувачі',
      'пользователи',
    ],
    action: () => navigate('/settings?section=team'),
  },
  {
    id: 'nav-analysis-setup',
    labelKey: 'nav.analysisSetup',
    descriptionKey: 'commandPalette.descriptions.analysisSetup',
    icon: Workflow,
    category: 'navigation',
    screen: 'analysis-setup',
    keywords: [
      'analysis',
      'setup',
      'criteria',
      'scripts',
      'routing',
      'настройка',
      'анализ',
      'критерии',
      'скрипты',
      'распределение',
      'налаштування',
    ],
    action: () => navigate('/analysis-setup'),
  },
  {
    id: 'nav-analysis-routing',
    labelKey: 'analysisSetup:steps.routing',
    descriptionKey: 'commandPalette.descriptions.analysisSetupRouting',
    icon: Shuffle,
    category: 'navigation',
    screen: 'analysis-setup',
    keywords: [
      'routing',
      'distribution',
      'rules',
      'маршрутизация',
      'распределение',
      'розподіл',
      'правила',
    ],
    action: () => navigate('/analysis-setup/routing'),
  },
  {
    id: 'nav-analysis-criteria',
    labelKey: 'analysisSetup:steps.criteria',
    descriptionKey: 'commandPalette.descriptions.analysisSetupCriteria',
    icon: ClipboardCheck,
    category: 'navigation',
    screen: 'analysis-setup',
    keywords: [
      'criteria',
      'categories',
      'evaluation',
      'критерии',
      'критерії',
      'категории',
      'категорії',
    ],
    action: () => navigate('/analysis-setup/categories'),
  },
  {
    id: 'nav-analysis-scripts',
    labelKey: 'analysisSetup:steps.scripts',
    descriptionKey: 'commandPalette.descriptions.analysisSetupScripts',
    icon: ScrollText,
    category: 'navigation',
    screen: 'analysis-setup',
    keywords: ['scripts', 'sales', 'скрипти', 'скрипты', 'сценарии'],
    action: () => navigate('/analysis-setup/scripts'),
  },
  {
    id: 'nav-analysis-sales-methodology',
    labelKey: 'analysisSetup:steps.salesMethodology',
    descriptionKey: 'commandPalette.descriptions.analysisSetupSalesMethodology',
    icon: BookOpenCheck,
    category: 'navigation',
    screen: 'analysis-setup',
    // The step exists behind the deal-state feature, the Deals screen's flag;
    // a plan below it lands on the overview, so no locked entry (the locked
    // Deals command is the one that explains the plan).
    featureFlag: 'enableDealStateSummary',
    hiddenWhenLocked: true,
    keywords: [
      'methodology',
      'sales methodology',
      'bant',
      'spin',
      'meddic',
      'методика',
      'методика продаж',
      'методика продажів',
      'metodología',
    ],
    action: () => navigate('/analysis-setup/sales-methodology'),
  },
  {
    id: 'nav-analysis-crm',
    labelKey: 'analysisSetup:steps.crm',
    descriptionKey: 'commandPalette.descriptions.analysisSetupCrm',
    icon: Database,
    category: 'navigation',
    screen: 'analysis-setup',
    keywords: ['crm', 'deal', 'update', 'срм', 'сделка', 'угода', 'crm-оновлення'],
    action: () => navigate('/analysis-setup/crm-updates'),
  },
  {
    id: 'nav-settings',
    labelKey: 'nav.settings',
    descriptionKey: 'commandPalette.descriptions.settings',
    icon: Settings,
    category: 'navigation',
    screen: 'settings',
    keywords: [
      'settings',
      'preferences',
      'config',
      'налаштування',
      'настройки',
      'notifications',
      'уведомления',
      'оповещения',
      'сповіщення',
    ],
    shortcutKey: '⌘,',
    action: () => navigate('/settings'),
  },
];

const getActionCommands = (navigate: NavigateFn): CommandItem[] => [
  // The assistant about the whole base (#765 Ф3). Its flag comes from the
  // `assistant` nav item; before the contour is released there is none.
  ...(isAssistantScopeReleased('org')
    ? [
        {
          id: 'action-ask-assistant',
          labelKey: 'commandPalette.actions.askAssistant',
          descriptionKey: 'commandPalette.actions.askAssistantDesc',
          icon: Sparkles,
          category: 'action' as const,
          screen: 'assistant',
          keywords: [
            'assistant',
            'ai',
            'ask',
            'question',
            'асистент',
            'ассистент',
            'спитати',
            'спросить',
            'asistente',
          ],
          action: () => navigate('/assistant'),
        },
      ]
    : []),
  {
    id: 'action-new-project',
    labelKey: 'commandPalette.actions.createNewProject',
    descriptionKey: 'commandPalette.actions.createNewProjectDesc',
    icon: Plus,
    category: 'action',
    screen: 'projects',
    capability: 'createProject',
    keywords: ['create', 'new', 'project', 'створити', 'создать', 'проєкт', 'проект'],
    action: () => navigate('/projects/new'),
  },
  {
    id: 'action-upload-record',
    labelKey: 'commandPalette.actions.uploadRecord',
    descriptionKey: 'commandPalette.actions.uploadRecordDesc',
    icon: Upload,
    category: 'action',
    screen: 'records',
    // The records screen is visible to every role, but uploading is not: this
    // command lands on a page whose upload button a supervisor never sees.
    capability: 'uploadRecords',
    keywords: [
      'upload',
      'record',
      'audio',
      'завантажити',
      'загрузить',
      'запис',
      'запись',
    ],
    action: () => navigate('/records'),
  },
  {
    id: 'action-export-records',
    labelKey: 'commandPalette.actions.exportRecords',
    descriptionKey: 'commandPalette.actions.exportRecordsDesc',
    icon: Download,
    category: 'action',
    screen: 'records',
    keywords: ['export', 'csv', 'download', 'експорт', 'экспорт', 'скачать'],
    action: () => navigate('/records?export=true'),
  },
  {
    id: 'action-view-analytics',
    labelKey: 'commandPalette.actions.viewAnalytics',
    descriptionKey: 'commandPalette.actions.viewAnalyticsDesc',
    icon: BarChart3,
    category: 'action',
    screen: 'call-stats',
    keywords: [
      'analytics',
      'stats',
      'statistics',
      'аналітика',
      'аналитика',
      'статистика',
    ],
    action: () => navigate('/analytics/call-stats'),
  },
  {
    id: 'action-invite-member',
    labelKey: 'commandPalette.actions.inviteMember',
    descriptionKey: 'commandPalette.actions.inviteMemberDesc',
    icon: Users,
    category: 'action',
    screen: 'users',
    keywords: ['invite', 'member', 'add', 'user', 'запросити', 'пригласить'],
    action: () => navigate('/settings?section=team'),
  },
];

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  recentItems?: string[];
  className?: string;
}

export function CommandPalette({
  isOpen,
  onClose,
  // recentItems - reserved for future use (internal state used instead)
  className,
}: CommandPaletteProps) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Text search for records via GET /records?search=...
  const RECORD_SEARCH_MIN_LEN = 3;
  const { apiClient } = useApiClient();
  const { t: tRecords } = useTranslation(['records']);
  const debouncedSearch = useDebounce(search, 400);
  const [recordResults, setRecordResults] = useState<RecordItem[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const searchRecords = useCallback(
    async (q: string) => {
      if (!q.trim() || q.length < RECORD_SEARCH_MIN_LEN) {
        setRecordResults([]);
        setRecordsLoading(false);
        return;
      }
      try {
        setRecordsLoading(true);
        const data = await apiClient.records.getRecords({
          search: q,
          limit: '5',
        });
        setRecordResults(toRecordItems(data.records, tRecords));
      } catch (err) {
        logError(
          'command_palette.search_failed',
          { query: q },
          err instanceof Error ? err : undefined
        );
        setRecordResults([]);
      } finally {
        setRecordsLoading(false);
      }
    },
    [apiClient, tRecords]
  );

  useEffect(() => {
    if (isOpen && debouncedSearch.length >= RECORD_SEARCH_MIN_LEN) {
      void searchRecords(debouncedSearch);
    } else {
      setRecordResults([]);
      setRecordsLoading(false);
    }
  }, [isOpen, debouncedSearch, searchRecords]);
  // Scroll selected item into view when navigating with keyboard
  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector<HTMLElement>(
      `[data-index="${selectedIndex}"]`
    );
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);
  const dialogRef = useFocusTrap<HTMLDivElement>(isOpen);
  // 'analysisSetup' is loaded explicitly (not just via defaultNS 'common')
  // because the analysis-setup sub-commands below reference it with an
  // explicit `analysisSetup:` prefix (e.g. 'analysisSetup:steps.routing') —
  // same pattern as CategoryCriteriaMatrix's cross-namespace t() calls.
  const { t } = useTranslation(['common', 'analysisSetup']);
  const navigate = useNavigate();
  const { appNavigate } = useAppNavigate();

  // Persisted recent items (max 5)
  const [recentItemIds, setRecentItemIds] = usePersistedState<string[]>(
    'aica-command-palette-recent',
    []
  );

  // All available commands, filtered to what the current role and feature
  // flags actually allow — the palette must not advertise pages that resolve
  // to Access Denied / Feature Not Available (2026-07-13 UX audit C-4).
  const { getVisibleScreens } = useNavigation();
  const { isEnabled: isFeatureEnabled, gate } = useFeatureFlags();
  const { canCreateProject, canUploadRecords } = useRole();
  const trialOpened = useTrialOpened();
  const allCommands = useMemo(() => {
    const visibleScreens = getVisibleScreens();
    const capabilities = {
      createProject: canCreateProject,
      uploadRecords: canUploadRecords,
    };
    return [...getNavigationCommands(appNavigate), ...getActionCommands(appNavigate)]
      .filter((cmd) => !cmd.screen || visibleScreens.includes(cmd.screen))
      .flatMap((cmd): CommandItem[] => {
        const flag =
          cmd.featureFlag ?? (cmd.screen ? SCREEN_FEATURE_FLAGS[cmd.screen] : undefined);
        if (!flag) return [cmd];
        // The plan is below the feature's floor: the screen is listed with a
        // lock, as in the menu (#1897) — it opens the screen that says so.
        const access = navItemAccess(gate(flag), isFeatureEnabled(flag));
        if (access === 'hidden' || (access === 'locked' && cmd.hiddenWhenLocked))
          return [];
        if (access === 'locked') return [{ ...cmd, lockedFlag: flag }];
        return [trialOpened(flag) ? { ...cmd, trialFlag: flag } : cmd];
      })
      .filter((cmd) => !cmd.capability || capabilities[cmd.capability]);
  }, [
    appNavigate,
    getVisibleScreens,
    isFeatureEnabled,
    gate,
    trialOpened,
    canCreateProject,
    canUploadRecords,
  ]);

  // The lock + plan name of a command above the plan (#1897).
  const renderLock = (cmd: CommandItem) => {
    if (!cmd.lockedFlag) return null;
    const plan = minPlanLabel(cmd.lockedFlag);
    return (
      <span
        className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-fg-subtle"
        data-testid={`command-lock-${cmd.id}`}
        title={plan ? t('nav.lockedPlan', { label: t(cmd.labelKey), plan }) : undefined}
      >
        <Lock className="h-3 w-3" aria-hidden="true" />
        {plan}
      </span>
    );
  };

  // «Pro · trial» of a command only the Pro trial opens (#2816), in the lock's
  // place — the trial's plan, not the feature's floor.
  const renderTrial = (cmd: CommandItem) => {
    if (!cmd.trialFlag) return null;
    return (
      <span
        className="ml-2 text-xs font-normal text-info-fg"
        data-testid={`command-trial-${cmd.id}`}
        title={t('proTrial.navLabel', { label: t(cmd.labelKey), plan: PRO_TRIAL_PLAN })}
      >
        {t('proTrial.label', { plan: PRO_TRIAL_PLAN })}
      </span>
    );
  };

  // Execute a command and track in recent items
  const executeCommand = (cmd: CommandItem) => {
    setRecentItemIds((prev) => {
      const filtered = prev.filter((id) => id !== cmd.id);
      return [cmd.id, ...filtered].slice(0, 5);
    });
    cmd.action();
    onClose();
  };

  // Recent commands resolved from IDs (only shown when not searching)
  const recentCommands = useMemo(() => {
    if (search) return [];
    return recentItemIds
      .map((id) => allCommands.find((cmd) => cmd.id === id))
      .filter(Boolean) as CommandItem[];
  }, [recentItemIds, allCommands, search]);

  // Filter commands based on search
  const filteredCommands = search
    ? allCommands.filter((cmd) => {
        const searchLower = search.toLowerCase();
        const label = t(cmd.labelKey).toLowerCase();
        const description = cmd.descriptionKey ? t(cmd.descriptionKey).toLowerCase() : '';
        return (
          label.includes(searchLower) ||
          description.includes(searchLower) ||
          cmd.keywords?.some((kw) => kw.includes(searchLower))
        );
      })
    : allCommands;

  // Build display list: recent section + main commands (when not searching)
  // For keyboard navigation purposes, all items in the visible list are counted
  const displayCommands = search
    ? filteredCommands
    : [...recentCommands, ...filteredCommands];

  // Group main commands by category
  const groupedCommands = filteredCommands.reduce(
    (acc, cmd) => {
      if (!acc[cmd.category]) {
        acc[cmd.category] = [];
      }
      const categoryCommands = acc[cmd.category];
      if (categoryCommands) {
        categoryCommands.push(cmd);
      }
      return acc;
    },
    {} as Record<string, CommandItem[]>
  );

  // Total navigable items = commands + record results
  const totalNavigable = displayCommands.length + recordResults.length;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < totalNavigable - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalNavigable - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex < displayCommands.length) {
          const selectedCommand = displayCommands[selectedIndex];
          if (selectedCommand) {
            executeCommand(selectedCommand);
          }
        } else {
          // Selected item is a record result
          const recordIndex = selectedIndex - displayCommands.length;
          const record = recordResults[recordIndex];
          if (record) {
            void navigate({ to: '/records', search: { id: record.id } });
            onClose();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedIndex, displayCommands, onClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // No AnimatePresence / exit animation on purpose: selecting a result closes
  // the palette in the same tick as a router navigation (startTransition), and
  // a frozen exit would leave the full-screen overlay stuck over the app —
  // same class as the RouteTransition/NotificationsDropdown freezes.
  if (!isOpen) return null;

  return (
    <m.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-palette-title"
      className="fixed inset-0 z-modal flex items-start justify-center bg-bg/80 backdrop-blur-sm"
      variants={fadeVariants}
      initial="hidden"
      animate="visible"
      transition={buildTransition('fast', 'smooth')}
      onClick={onClose}
      onKeyDown={(e) =>
        (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') && onClose()
      }
      tabIndex={-1}
    >
      <m.div
        ref={dialogRef}
        className={cn(
          'mt-24 w-full max-w-2xl rounded-lg border border-border bg-bg-elevated shadow-lg',
          className
        )}
        variants={scaleVariants}
        initial="hidden"
        animate="visible"
        transition={buildTransition('fast', 'emphasized')}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          e.stopPropagation();
        }}
      >
        {/* Visually hidden title for screen readers */}
        <h2 id="command-palette-title" className="sr-only">
          {t('commandPalette.title')}
        </h2>
        {/* Search Input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-5 w-5 text-fg-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('commandPalette.searchPlaceholder')}
            aria-label={t('commandPalette.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedIndex(0);
            }}
            className={cn(
              'flex-1 bg-transparent text-sm text-fg placeholder-fg-subtle',
              'focus:outline-none'
            )}
          />
          <button
            onClick={onClose}
            className={cn(
              CONTROL_SQUARE.md,
              'rounded-sm p-2 flex items-center justify-center text-fg-muted transition-all duration-150 hover:bg-bg-subtle hover:text-fg'
            )}
            aria-label={t('common:common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Commands List */}
        <div
          ref={listRef}
          role="listbox"
          aria-label={t('commandPalette.availableCommands')}
          className="max-h-96 overflow-y-auto p-2"
        >
          {/* Recent items section — only shown when not searching */}
          {!search && recentCommands.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-fg-subtle uppercase tracking-wide">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {t('commandPalette.categories.recent')}
              </div>
              <div className="space-y-0.5">
                {recentCommands.map((cmd) => {
                  const globalIndex = displayCommands.indexOf(cmd);
                  const isSelected = globalIndex === selectedIndex;
                  const Icon = cmd.icon;

                  return (
                    <button
                      key={`recent-${cmd.id}`}
                      role="option"
                      aria-selected={isSelected}
                      data-index={globalIndex}
                      onClick={() => executeCommand(cmd)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-button px-3 py-3 text-left',
                        'transition-all duration-150',
                        isSelected
                          ? 'bg-primary-dim text-primary-fg'
                          : 'text-fg-muted hover:bg-bg-subtle hover:text-fg'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          isSelected ? 'text-primary' : 'text-fg-subtle'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {t(cmd.labelKey)}
                          {renderLock(cmd)}
                          {renderTrial(cmd)}
                        </p>
                        {cmd.descriptionKey && (
                          <p className="text-xs text-fg-subtle truncate">
                            {t(cmd.descriptionKey)}
                          </p>
                        )}
                      </div>
                      {cmd.shortcutKey && (
                        <kbd className="ml-auto shrink-0 rounded border border-border bg-bg px-1.5 py-0.5 text-xs text-fg-subtle font-mono">
                          {cmd.shortcutKey}
                        </kbd>
                      )}
                      {isSelected && !cmd.shortcutKey && (
                        <ArrowRight className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Main grouped commands */}
          {Object.entries(groupedCommands).map(([category, commands], categoryIndex) => (
            <div key={category} className={categoryIndex > 0 ? 'mt-4' : ''}>
              <div className="px-2 py-1.5 text-xs font-medium text-fg-subtle uppercase tracking-wide">
                {t(`commandPalette.categories.${category}`)}
              </div>
              <div className="space-y-0.5">
                {commands.map((cmd) => {
                  const globalIndex =
                    recentCommands.length + filteredCommands.indexOf(cmd);
                  const isSelected = globalIndex === selectedIndex;
                  const Icon = cmd.icon;

                  return (
                    <button
                      key={cmd.id}
                      role="option"
                      aria-selected={isSelected}
                      data-index={globalIndex}
                      onClick={() => executeCommand(cmd)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-button px-3 py-3 text-left',
                        'transition-all duration-150',
                        isSelected
                          ? 'bg-primary-dim text-primary-fg'
                          : 'text-fg-muted hover:bg-bg-subtle hover:text-fg'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          isSelected ? 'text-primary' : 'text-fg-subtle'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {t(cmd.labelKey)}
                          {renderLock(cmd)}
                          {renderTrial(cmd)}
                        </p>
                        {cmd.descriptionKey && (
                          <p className="text-xs text-fg-subtle truncate">
                            {t(cmd.descriptionKey)}
                          </p>
                        )}
                      </div>
                      {cmd.shortcutKey && (
                        <kbd className="ml-auto shrink-0 rounded border border-border bg-bg px-1.5 py-0.5 text-xs text-fg-subtle font-mono">
                          {cmd.shortcutKey}
                        </kbd>
                      )}
                      {isSelected && !cmd.shortcutKey && (
                        <ArrowRight className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredCommands.length === 0 &&
            recordResults.length === 0 &&
            !recordsLoading && (
              <div className="py-8 text-center">
                <Search className="mx-auto h-8 w-8 text-fg-subtle" />
                <p className="mt-2 text-sm text-fg-muted">
                  {t('commandPalette.noResultsFound')}
                </p>
                <p className="mt-1 text-xs text-fg-subtle">
                  {t('commandPalette.tryDifferentSearch')}
                </p>
              </div>
            )}

          {/* Record search results */}
          {(recordResults.length > 0 || recordsLoading) &&
            search.length >= RECORD_SEARCH_MIN_LEN && (
              <div className="mt-4">
                <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-fg-subtle uppercase tracking-wide">
                  {recordsLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileAudio className="h-3 w-3" />
                  )}
                  {t('commandPalette.categories.records', 'Records')}
                </div>
                <div className="space-y-0.5">
                  {recordResults.map((record, i) => {
                    const globalIndex = displayCommands.length + i;
                    const isSelected = globalIndex === selectedIndex;
                    const Icon =
                      record.channel === 'chat'
                        ? MessageSquare
                        : record.callDirection === 'inbound'
                          ? PhoneIncoming
                          : record.callDirection === 'outbound'
                            ? PhoneOutgoing
                            : Phone;

                    // `participantName` is already "name, else number, else
                    // Unknown" (toRecordItem decides that); past "Unknown" the
                    // record goes by its external id or the localized "<type>
                    // <id>" title — never the CRM activity's own title, which is
                    // in the portal's language (#1104).
                    const displayName =
                      (record.participantName &&
                      record.participantName !== tRecords('records.unknown')
                        ? record.participantName
                        : null) ||
                      record.externalId ||
                      record.title;

                    // Subtitle: direction + agent + duration + date
                    const directionLabel =
                      record.callDirection === 'inbound'
                        ? tRecords('records.inbound', 'Inbound')
                        : record.callDirection === 'outbound'
                          ? tRecords('records.outbound', 'Outbound')
                          : null;
                    const channelLabel =
                      record.channel === 'chat'
                        ? tRecords('records.chat', 'Chat')
                        : tRecords('records.call', 'Call');
                    const subtitle = [
                      directionLabel || channelLabel,
                      record.agentName,
                      record.duration,
                      record.createdAt ? formatDateLocale(record.createdAt) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');

                    return (
                      <button
                        key={record.id}
                        role="option"
                        aria-selected={isSelected}
                        data-index={globalIndex}
                        onClick={() => {
                          void navigate({
                            to: '/records',
                            search: { id: record.id },
                          });
                          onClose();
                        }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-button px-3 py-3 text-left',
                          'transition-all duration-150',
                          isSelected
                            ? 'bg-primary-dim text-primary-fg'
                            : 'text-fg-muted hover:bg-bg-subtle hover:text-fg'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 shrink-0',
                            isSelected ? 'text-primary' : 'text-fg-subtle'
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{displayName}</p>
                          <p className="text-xs text-fg-subtle truncate">{subtitle}</p>
                        </div>
                        {isSelected && (
                          <ArrowRight className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-fg-subtle">
          <div className="flex gap-4">
            <span>
              <kbd className="rounded-sm border border-border bg-bg px-1.5 py-0.5">
                ↑↓
              </kbd>{' '}
              {t('commandPalette.footer.navigate')}
            </span>
            <span>
              <kbd className="rounded-sm border border-border bg-bg px-1.5 py-0.5">↵</kbd>{' '}
              {t('commandPalette.footer.select')}
            </span>
            <span>
              <kbd className="rounded-sm border border-border bg-bg px-1.5 py-0.5">
                Esc
              </kbd>{' '}
              {t('commandPalette.footer.close')}
            </span>
          </div>
        </div>
      </m.div>
    </m.div>
  );
}
